import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box, Card, Chip, IconButton, InputAdornment, TextField, Tooltip, Typography,
} from '@mui/material';
import { AccountBalance, Refresh, Search } from '@mui/icons-material';
import axios from '../utils/axios';
import CandleChartModal from './CandleChartModal';
import TickerLink from './TickerLink';
import DataTable, { DataTableColumn } from '../design/DataTable';
import PnlText from '../design/PnlText';
import UpdatedStamp from '../design/UpdatedStamp';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { formatCurrency, formatShares, formatDateTime } from '../design/format';
import { pnlColors } from '../design/theme';

interface Position {
  Id: number;
  Ticker: string;
  Investment: number;
  PurchasePrice: number;
  PurchaseTime: string | null;
  Shares: number;
  PortfolioId: number;
  StockId: number;
  TraderId: number;
  CurrentPrice: number;
  CurrentChange: number | null;
  CurrentValue: number;
}

interface CurrentPositionsProps {
  traderId: number;
}

type SortField =
  | 'Ticker' | 'PurchaseTime' | 'Shares' | 'PurchasePrice'
  | 'CurrentPrice' | 'Investment' | 'CurrentValue' | 'CurrentChange';

const ITEMS_PER_PAGE = 10;

const COLUMNS: DataTableColumn<Position>[] = [
  {
    key: 'Ticker', label: 'Ticker', sortable: true,
    render: (p) => <TickerLink ticker={p.Ticker} />,
  },
  { key: 'PurchaseTime', label: 'Purchase Time', sortable: true, minWidth: 120, render: (p) => formatDateTime(p.PurchaseTime) },
  { key: 'Shares', label: 'Shares', sortable: true, align: 'right', render: (p) => formatShares(p.Shares) },
  { key: 'PurchasePrice', label: 'Purchase Price', sortable: true, align: 'right', render: (p) => formatCurrency(p.PurchasePrice) },
  { key: 'CurrentPrice', label: 'Current Price', sortable: true, align: 'right', render: (p) => formatCurrency(p.CurrentPrice) },
  { key: 'Investment', label: 'Investment', sortable: true, align: 'right', render: (p) => formatCurrency(p.Investment) },
  {
    key: 'CurrentValue', label: 'Current Value', sortable: true, align: 'right',
    render: (p) => (
      <Typography variant="body2" component="span"
        sx={{ fontWeight: 700, color: p.CurrentValue >= p.Investment ? pnlColors.gain : pnlColors.loss }}>
        {formatCurrency(p.CurrentValue)}
      </Typography>
    ),
  },
  {
    key: 'CurrentChange', label: 'Change', sortable: true, align: 'right',
    render: (p) => <PnlText value={p.CurrentChange} format="percent" variant="body2" />,
  },
];

const CurrentPositions: React.FC<CurrentPositionsProps> = ({ traderId }) => {
  const [positions, setPositions] = useState<Position[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<SortField>('PurchaseTime');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const fetchSeq = useRef(0);

  const visiblePositions = useMemo(() => {
    const q = search.trim().toUpperCase();
    const filtered = q ? positions.filter((p) => p.Ticker.toUpperCase().includes(q)) : positions;
    return [...filtered].sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      // Nulls (e.g. Change before first price refresh) always sink to the end.
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      let cmp: number;
      if (sortBy === 'Ticker') cmp = (av as string).localeCompare(bv as string);
      else if (sortBy === 'PurchaseTime') cmp = new Date(av as string).getTime() - new Date(bv as string).getTime();
      else cmp = (av as number) - (bv as number);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [positions, search, sortBy, sortDir]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, sortBy, sortDir]);

  const fetchPositions = useCallback(async (silent = false) => {
    const seq = ++fetchSeq.current;
    if (!silent) setLoading(true);
    try {
      const response = await axios.get(`/portfolio/${traderId}/positions`);
      if (seq !== fetchSeq.current) return;  // stale response; a newer request is in flight
      setPositions(response.data);
      setUpdatedAt(new Date());
      setError(null);
    } catch (err) {
      if (seq !== fetchSeq.current) return;
      console.error('Error fetching positions:', err);
      if (!silent) setError('Failed to fetch current positions');
    } finally {
      if (seq === fetchSeq.current && !silent) setLoading(false);
    }
  }, [traderId]);

  useEffect(() => { fetchPositions(); }, [fetchPositions]);
  useAutoRefresh(() => fetchPositions(true));

  const handleSort = (key: string) => {
    const field = key as SortField;
    const isAsc = sortBy === field && sortDir === 'asc';
    setSortDir(isAsc ? 'desc' : 'asc');
    setSortBy(field);
  };

  const pageCount = Math.ceil(visiblePositions.length / ITEMS_PER_PAGE);
  // Safety net for data shrinking under the user (e.g. a silent refresh drops
  // rows while they're on a later page); the [search, sortBy, sortDir] effect
  // still handles intent-driven resets to page 1.
  const safePage = Math.min(currentPage, Math.max(1, pageCount));
  const pageRows = visiblePositions.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  if (error) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 3 }}>
          Current Positions
        </Typography>
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <Typography color="error" variant="h6">{error}</Typography>
          <IconButton onClick={() => fetchPositions()} sx={{ mt: 2 }} color="primary" aria-label="Retry">
            <Refresh />
          </IconButton>
        </Card>
      </Box>
    );
  }

  if (!loading && positions.length === 0) {
    return (
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.dark', mb: 3 }}>
          Current Positions
        </Typography>
        <Card sx={{ textAlign: 'center', p: 4 }}>
          <AccountBalance sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No current positions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This trader doesn't have any active positions yet.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} flexWrap="wrap" gap={1}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.dark' }}>
          Current Positions
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <UpdatedStamp updatedAt={updatedAt} />
          <Chip
            label={
              search.trim()
                ? `${visiblePositions.length} of ${positions.length} positions`
                : `${positions.length} positions`
            }
            color="primary" variant="outlined" size="small"
          />
          <Tooltip title="Refresh positions">
            <IconButton onClick={() => fetchPositions()} size="small" color="primary">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TextField
        size="small"
        placeholder="Search ticker…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, width: 220 }}
      />

      <DataTable
        columns={COLUMNS}
        rows={pageRows}
        rowKey={(p) => p.Id}
        loading={loading}
        skeletonRows={5}
        emptyMessage={`No positions match "${search.trim()}"`}
        sortBy={sortBy}
        sortDir={sortDir}
        onSortChange={handleSort}
        onRowClick={(p) => setSelectedPosition(p)}
        rowAriaLabel={(p) => `View chart for ${p.Ticker}`}
        page={safePage}
        pageCount={pageCount}
        onPageChange={setCurrentPage}
      />

      <CandleChartModal
        open={selectedPosition !== null}
        onClose={() => setSelectedPosition(null)}
        ticker={selectedPosition?.Ticker ?? ''}
        tradeTime={selectedPosition?.PurchaseTime ?? null}
        action="BUY"
        tradePrice={selectedPosition?.PurchasePrice ?? null}
        traderId={traderId}
        shares={selectedPosition?.Shares ?? null}
        changePercent={selectedPosition?.CurrentChange ?? null}
      />
    </Box>
  );
};

export default CurrentPositions;
